/*
 * Profile-wizard persistence — SELF subject, REAL account only.
 *
 * The self subject reads/writes the real Cognito profile attributes through
 * the existing seam (src/auth/cognitoProfile.ts). No seeded demo profile
 * ships anywhere (LOCKED no-fabricated-data posture): the wizard operates on
 * the signed-in account's stored values, or an empty form.
 *
 * Attribute homes:
 *  - given_name / family_name / birthdate (YYYY-MM-DD) / gender /
 *    phone_number (E.164) / address / locale  → Cognito standard attributes.
 *  - nationality, residence country, city, emergency contact, T-shirt size,
 *    self-describe text, dial-code selections → the consumer pool has NO
 *    standard attribute for these. Until the participant store lands
 *    (my-api / settings — see TODO(collision-D) in wizardConfig.ts), they
 *    persist in a per-user localStorage record on this device. That is the
 *    user's own real input, clearly a device-local extension — never
 *    fabricated, never shown for another account.
 *    // TODO(participant-store): move extras to the my-api participant
 *    // profile endpoint when it ships; localStorage is a stopgap seam.
 */
import {
  getProfile, updateProfile, type CognitoProfile,
} from '../../auth/cognitoProfile'
import { refreshSession } from '../../auth/cognitoAuth'
import { loadSession, storeSession } from '../../auth/session'
import { EMPTY_DRAFT, MONTHS, fromE164, toE164, type WizardDraft } from './wizardModel'

const EXTRAS_KEY = (sub: string) => `mytrumin.profileExtras.v1.${sub}`

/** Display label ↔ BCP-47 code for the Cognito `locale` attribute. */
const LOCALE_BY_LABEL: Record<string, string> = {
  English: 'en', 'العربية': 'ar', 'Français': 'fr', 'Español': 'es',
  Deutsch: 'de', Italiano: 'it', 'Português': 'pt', '中文': 'zh',
}
const LABEL_BY_LOCALE: Record<string, string> = Object.fromEntries(
  Object.entries(LOCALE_BY_LABEL).map(([label, code]) => [code, label]),
)

interface Extras {
  nationality?: string
  natIso?: string
  resCountry?: string
  resIso?: string
  city?: string
  dial?: string
  selfText?: string
  ecName?: string
  ecDial?: string
  ecPhone?: string
  ecRel?: string
  shirt?: string
}

function readExtras(sub: string): Extras {
  try {
    const raw = localStorage.getItem(EXTRAS_KEY(sub))
    return raw ? (JSON.parse(raw) as Extras) : {}
  } catch {
    return {}
  }
}

function writeExtras(sub: string, e: Extras): void {
  localStorage.setItem(EXTRAS_KEY(sub), JSON.stringify(e))
}

function draftFromStores(p: CognitoProfile, extras: Extras): WizardDraft {
  const d: WizardDraft = { ...EMPTY_DRAFT }
  d.first = p.givenName ?? ''
  d.last = p.familyName ?? ''
  // birthdate YYYY-MM-DD → explicit Day / Month-name / Year selects
  if (p.birthdate && /^\d{4}-\d{2}-\d{2}$/.test(p.birthdate)) {
    const [y, m, day] = p.birthdate.split('-')
    d.dobY = String(Number(y))
    d.dobM = MONTHS[Number(m) - 1] ?? ''
    d.dobD = String(Number(day))
  }
  d.gender = p.gender ?? ''
  if (p.phone) {
    const parsed = fromE164(p.phone)
    if (parsed) {
      d.dial = parsed.dialLabel
      d.phone = parsed.national
    } else {
      d.phone = p.phone.replace(/^\+/, '') // unknown dial — surface digits honestly
    }
  }
  d.addr = p.address ?? ''
  d.language = p.locale ? (LABEL_BY_LOCALE[p.locale] ?? '') : ''
  d.nationality = extras.nationality ?? ''
  d.natIso = extras.natIso ?? ''
  d.resCountry = extras.resCountry ?? ''
  d.resIso = extras.resIso ?? ''
  d.city = extras.city ?? ''
  if (extras.dial) d.dial = extras.dial
  d.selfText = extras.selfText ?? ''
  d.ecName = extras.ecName ?? ''
  d.ecDial = extras.ecDial ?? ''
  d.ecPhone = extras.ecPhone ?? ''
  d.ecRel = extras.ecRel ?? ''
  d.shirt = extras.shirt ?? ''
  return d
}

/** Load the signed-in account's profile into a wizard draft.
 *  Returns null when there is no session (the wizard renders honestly empty —
 *  routes are only reachable authed via App.tsx, but never assume). */
export async function loadSelfDraft(): Promise<WizardDraft | null> {
  const session = loadSession()
  if (!session) return null
  const profile = await getProfile(session.accessToken)
  const extras = readExtras(session.claims.sub)
  return draftFromStores(profile, extras)
}

/** Persist the draft on Save (canon: Save profile). Cognito attributes via the
 *  existing seam; non-attribute fields to the device-local extras record. */
export async function saveSelfDraft(d: WizardDraft): Promise<void> {
  const session = loadSession()
  if (!session) throw new Error('not signed in — nothing was saved')
  const p: CognitoProfile = {
    givenName: d.first,
    familyName: d.last,
  }
  if (d.dobD && d.dobM && d.dobY) {
    const m = MONTHS.indexOf(d.dobM) + 1
    p.birthdate = `${d.dobY.padStart(4, '0')}-${String(m).padStart(2, '0')}-${d.dobD.padStart(2, '0')}`
  }
  if (d.gender) p.gender = d.gender
  const e164 = toE164(d.dial || 'AE +971', d.phone)
  if (e164) p.phone = e164
  if (d.addr) p.address = d.addr
  if (d.language && LOCALE_BY_LABEL[d.language]) p.locale = LOCALE_BY_LABEL[d.language]
  await updateProfile(session.accessToken, p)
  writeExtras(session.claims.sub, {
    nationality: d.nationality, natIso: d.natIso,
    resCountry: d.resCountry, resIso: d.resIso, city: d.city,
    dial: d.dial, selfText: d.selfText,
    ecName: d.ecName, ecDial: d.ecDial, ecPhone: d.ecPhone, ecRel: d.ecRel,
    shirt: d.shirt,
  })
  // The ID token was minted at sign-in, BEFORE this save, so its
  // given_name/family_name claims are stale (or absent for a first-time
  // profile). Everything that reads the signed-in name from the token claims —
  // notably the wallet ticket stub's Participant line (src/screens/Tickets.tsx)
  // — would keep rendering a blank "—" until the next sign-in
  // (BOARDROOM-2026-08-14-100859). Refresh the session so loadSession().claims
  // carries the name we just wrote. BEST-EFFORT: a refresh failure (no stored
  // refresh token, or a transient Cognito error) must NOT fail the save — the
  // attributes are already persisted and reconcile on the next sign-in; nothing
  // is fabricated.
  if (session.refreshToken) {
    try {
      const username =
        session.claims['cognito:username'] || session.claims.email || session.claims.sub
      const tokens = await refreshSession(username, session.refreshToken)
      storeSession(tokens)
    } catch {
      /* keep the existing session; the saved name reconciles on next sign-in */
    }
  }
}
